import { useQueryClient } from '@umijs/max';
import { useStoreApi } from '@xyflow/react';
import { produce } from 'immer';
import { useCallback } from 'react';
import { CommonNodeData, Edge, Node } from '../types';
import { useSyncDraft } from './use-sync-draft';
import { useWorkflowReadOnly } from './use-workflow';

type NodeDataUpdatePayload = {
  id: string;
  data: Partial<CommonNodeData>;
  options?: {
    // 是否使用防抖
    debounced?: boolean;
  };
};

export const useNodeDataUpdate = () => {
  const store = useStoreApi<Node, Edge>();
  const { doSyncWorkflowDraft, debouncedSyncWorkflowDraft } = useSyncDraft();
  const { getNodesReadOnly } = useWorkflowReadOnly();

  const handleNodeDataUpdate = useCallback(
    ({ id, data }: NodeDataUpdatePayload) => {
      const { nodes, setNodes } = store.getState();
      const newNodes = produce(nodes, (draft) => {
        const currentNode = draft.find((node) => node.id === id);
        if (currentNode) currentNode.data = { ...currentNode.data, ...data };
      });
      setNodes(newNodes);
    },
    [store],
  );

  const handleNodeDataUpdateWithSyncDraft = useCallback(
    (payload: NodeDataUpdatePayload) => {
      if (getNodesReadOnly()) return;

      handleNodeDataUpdate({
        id: payload.id,
        data: payload.data,
      });

      if (payload.options?.debounced) return doSyncWorkflowDraft();
      return debouncedSyncWorkflowDraft.run();
    },
    [doSyncWorkflowDraft, handleNodeDataUpdate, getNodesReadOnly],
  );

  return {
    handleNodeDataUpdate,
    handleNodeDataUpdateWithSyncDraft,
  };
};
